/**
 * DeleteSpaceSection — ștergerea (soft) a spațiului curent, doar pentru owner.
 *
 * Dialogul arată cu cine e partajat spațiul (numărul total + primii 5), ce
 * conține, și cere numele spațiului plus parola master. Pe server nimic nu se
 * șterge: spațiul devine inaccesibil pentru toți membrii.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { tenantsApi } from '@/api/tenants';
import Modal from '@/components/Modal';
import { reauthHash } from '@/lib/reauth';
import { useAuthStore } from '@/stores/auth';

interface Props {
  tenantId: string;
  tenantName: string;
}

export default function DeleteSpaceSection({ tenantId, tenantName }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <p className="text-xs text-[var(--text-muted)] mb-3">{t('space_delete.section_hint')}</p>
      <button
        type="button"
        className="btn-outline text-sm"
        style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
        onClick={() => setOpen(true)}
        data-testid="space-delete-open"
      >
        {t('space_delete.open')}
      </button>
      {open && (
        <DeleteSpaceDialog
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function DeleteSpaceDialog({ tenantId, tenantName, onClose }: Props & { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const removeTenant = useAuthStore((s) => s.removeTenant);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const preview = useQuery({
    queryKey: ['tenant-deletion-preview', tenantId],
    queryFn: () => tenantsApi.deletionPreview(tenantId),
    staleTime: 0,
  });

  const nameMatches = name.trim() === tenantName;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameMatches) {
      setError(t('space_delete.name_mismatch'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const authHash = await reauthHash(password);
      if (!authHash) {
        setError(t('space_delete.wrong_password'));
        return;
      }
      await tenantsApi.deleteTenant(tenantId, {
        auth_hash: authHash,
        confirm_name: name.trim(),
      });
      setPassword('');
      // Nimic din cache-ul spațiului șters nu mai e valabil.
      queryClient.clear();
      removeTenant(tenantId);
      navigate('/vault');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t('space_delete.failed'));
    } finally {
      setBusy(false);
    }
  };

  const data = preview.data;
  const hidden = data ? data.other_member_count - data.other_members.length : 0;

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      maxWidth="max-w-lg"
      title={
        <h2 className="display-title-sm">{t('space_delete.modal_title', { name: tenantName })}</h2>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="space-delete-dialog">
        {preview.isLoading && (
          <p className="text-sm text-[var(--text-muted)]">{t('space_delete.loading')}</p>
        )}
        {preview.isError && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {preview.error instanceof ApiError ? preview.error.detail : t('space_delete.failed')}
          </p>
        )}

        {data && (
          <div
            className="rounded px-3 py-2 text-sm space-y-2"
            style={{
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
              color: 'var(--text)',
            }}
          >
            {data.other_member_count === 0 ? (
              <p>{t('space_delete.shared_none')}</p>
            ) : (
              <>
                <p className="font-medium" data-testid="space-delete-shared">
                  {t('space_delete.shared', { count: data.other_member_count })}
                </p>
                <ul className="text-xs space-y-0.5" data-testid="space-delete-members">
                  {data.other_members.map((m) => (
                    <li key={m.email}>
                      <span className="font-mono">{m.email}</span>{' '}
                      <span className="text-[var(--text-muted)]">({m.role})</span>
                    </li>
                  ))}
                  {hidden > 0 && (
                    <li className="text-[var(--text-muted)]" data-testid="space-delete-more">
                      {t('space_delete.and_more', { count: hidden })}
                    </li>
                  )}
                </ul>
              </>
            )}
            <p className="text-xs">
              {t('space_delete.contents', {
                credentials: data.credential_count,
                notes: data.note_count,
                shared: data.shared_item_count,
              })}
            </p>
          </div>
        )}

        <p className="text-xs text-[var(--text-muted)]">{t('space_delete.soft_note')}</p>

        <div>
          <label
            htmlFor="space-delete-name"
            className="block text-xs font-medium text-[var(--text)] mb-1"
          >
            {t('space_delete.name_label')}: <span className="font-mono">{tenantName}</span>
          </label>
          <input
            id="space-delete-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            disabled={busy}
          />
        </div>
        <div>
          <label
            htmlFor="space-delete-password"
            className="block text-xs font-medium text-[var(--text)] mb-1"
          >
            {t('space_delete.password_label')}
          </label>
          <input
            id="space-delete-password"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
        </div>

        {error && (
          <div
            className="text-xs rounded px-3 py-2"
            style={{
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
            }}
            data-testid="space-delete-error"
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn-outline text-sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary text-sm"
            style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
            disabled={busy || !data || !nameMatches || !password}
            data-testid="space-delete-confirm"
          >
            {busy ? t('space_delete.deleting') : t('space_delete.confirm')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
